import { useRef, useState } from 'react';

interface Props {
  /** Описание ожидаемого ответа, как его прислал сервер. */
  payload: string | null;
  onAnswer: (value: string) => void;
}

interface Prompt {
  ask: 'time' | 'duration';
  /** Частые ответы в минутах — чтобы обойтись одним касанием. */
  hints?: number[];
}

/** Разбор описания. Сервер в него не смотрит, формат целиком на совести клиента. */
function parse(payload: string | null): Prompt | null {
  if (!payload) return null;
  try {
    const parsed = JSON.parse(payload) as Prompt;
    return parsed.ask === 'time' || parsed.ask === 'duration' ? parsed : null;
  } catch {
    // Сообщение пришло от версии сервера, которую этот клиент не знает:
    // показываем просто текст вопроса, отвечать можно обычным сообщением
    return null;
  }
}

function label(minutes: number): string {
  if (minutes === 0) return 'без запаса';
  if (minutes < 60) return `${minutes} мин`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} ч` : `${hours} ч ${rest}`;
}

/**
 * Ответ на вопрос бота выбором, а не набором текста.
 *
 * Для времени — поле `type="time"`: на телефоне оно открывает системные часы,
 * и не приходится попадать по цифрам. Для длительности — готовые варианты
 * плюс то же поле на случай нестандартного ответа.
 */
export function BotPrompt({ payload, onAnswer }: Props) {
  const prompt = parse(payload);
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  /** Открывает системный выбор времени, не заставляя целиться в поле. */
  function openPicker() {
    const input = inputRef.current;
    if (!input) return;
    // showPicker появился не во всех браузерах; где его нет — обычный фокус,
    // и на телефоне часы всё равно откроются
    const withPicker = input as HTMLInputElement & { showPicker?: () => void };
    if (typeof withPicker.showPicker === 'function') {
      try {
        withPicker.showPicker();
        return;
      } catch {
        // Браузер может запретить вызов вне жеста — тогда просто фокус
      }
    }
    input.focus();
    input.click();
  }

  if (!prompt) return null;

  return (
    <div className="bot-prompt">
      {prompt.ask === 'duration' && prompt.hints && (
        <div className="bot-hints">
          {prompt.hints.map((minutes) => (
            <button
              key={minutes}
              type="button"
              className="ghost"
              onClick={() => onAnswer(String(minutes))}
            >
              {label(minutes)}
            </button>
          ))}
        </div>
      )}

      <div className="bot-answer">
        {/*
          Поле скрыто за кнопкой: нажатие сразу открывает системные часы,
          а не ставит курсор в поле, где время пришлось бы набирать.
          showPicker есть не везде — если его нет, остаётся обычное поле
          выбора времени, которое на телефоне всё равно открывает часы.
        */}
        <input
          ref={inputRef}
          type="time"
          className="bot-time"
          value={value}
          onChange={(event) => {
            const picked = event.target.value;
            setValue(picked);
            // Выбрали на часах — отвечаем сразу: подтверждать кнопкой
            // то, что уже выбрано, лишнее действие
            if (picked) onAnswer(picked);
          }}
          aria-label={prompt.ask === 'time' ? 'Время' : 'Длительность'}
        />
        <button type="button" onClick={openPicker}>
          {prompt.ask === 'time' ? 'Выбрать время' : 'Выбрать своё'}
        </button>
      </div>
    </div>
  );
}
